import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Card,
  Space,
  message,
  Spin
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faBookmark, faCog, faChevronDown, faChevronUp, faCheckCircle, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
// AdminLayout now provided at router level
import EventTypeService from '../../services/EventTypeService';
import BookingService from '../../services/BookingService';
import EventTypeManager from './EventTypeManager';
// import CalendarManager from './CalendarManager';
import BookingManager from './BookingManager';
import AppointmentSettings from './AppointmentSettings';
import AvailabilityManager from './AvailabilityManager';
import EventTypeGroupManager from './EventTypeGroupManager';
import { usePageHeader } from '../../layouts/AdminLayout';
import {
  PageContainer,
  StatsCards,
  StatsCard,
  StatNumber,
  StatLabel
} from './NewStyled';
import {
  StyledTabs,
  TabBadge
} from './NewStyled';
import HeartLoader from '../../components/shared/HeartLoader';



const NewEventManagement = observer(() => {
  const [activeTab, setActiveTab] = useState('bookings');
  const [loading, setLoading] = useState(true);
  const [bookingEventTypeFilter, setBookingEventTypeFilter] = useState(null);
  const [isStatsExpanded, setIsStatsExpanded] = useState(false);
  const [stats, setStats] = useState({
    totalEventTypes: 0,
    confirmedBookings: 0,
    pendingBookings: 0,
    completedBookings: 0,
    cancelledBookings: 0,
    totalBookings: 0,
    eventTypeStats: []
  });
  const { setPageHeader } = usePageHeader();

  // Set page header
  useEffect(() => {
    setPageHeader({ title: 'Bookings' });
  }, [setPageHeader]);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const handleEventTypeClick = (eventTypeId) => {
    // If clicking the same tag that's already selected, deselect it
    if (bookingEventTypeFilter === eventTypeId) {
      setBookingEventTypeFilter(null);
    } else {
      setBookingEventTypeFilter(eventTypeId);
      setActiveTab('bookings');
    }
  };

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      
      // Get real statistics from APIs
      const [eventTypesResponse, bookingStatsResponse] = await Promise.all([
        EventTypeService.getEventTypes(), // This now includes booking counts
        BookingService.getBookingStats()
      ]);

      // Count event types
      const totalEventTypes = eventTypesResponse.success ? 
        (eventTypesResponse.data?.eventTypes?.length || 0) : 0;

      // Get booking statistics with better error handling
      let bookingStats = {};
      if (bookingStatsResponse.success && bookingStatsResponse.data) {
        // The response has a nested structure: { success: true, data: { success: true, data: {...} } }
        bookingStats = bookingStatsResponse.data.data || bookingStatsResponse.data;
      } else {
        console.warn('Booking stats API failed:', bookingStatsResponse.error);
      }

      // Get event types with booking counts for the cards
      const eventTypesWithBookings = eventTypesResponse.success && eventTypesResponse.data?.eventTypes 
        ? eventTypesResponse.data.eventTypes 
        : [];

      // Use bookingsByStatus.confirmed as the total confirmed count (all time)
      const confirmedCount = bookingStats.confirmedBookings || 0;
      const pendingCount = bookingStats.pendingBookings || 0;

      setStats({
        totalEventTypes,
        confirmedBookings: confirmedCount,
        pendingBookings: pendingCount,
        completedBookings: bookingStats.completedBookings || 0,
        cancelledBookings: bookingStats.cancelledBookings || 0,
        totalBookings: bookingStats.totalBookings || 0,
        eventTypeStats: eventTypesWithBookings
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      message.error('Failed to load dashboard statistics');
      // Fallback to mock data
      setStats({
        totalEventTypes: 3,
        confirmedBookings: 24,
        pendingBookings: 5,
        completedBookings: 892,
        cancelledBookings: 8,
        totalBookings: 916,
        eventTypeStats: [
          { _id: '1', name: 'Heart Screening', color: '#e74c3c', bookingCount: 450 },
          { _id: '2', name: 'Blood Pressure Check', color: '#3498db', bookingCount: 320 },
          { _id: '3', name: 'Diabetes Screening', color: '#2ecc71', bookingCount: 146 }
        ]
      });
      setLoading(false);
    }
  };

  const tabItems = [
    {
      key: 'bookings',
      label: (
        <Space>
          <FontAwesomeIcon icon={faCheckCircle} />
          Bookings
          <TabBadge color="#1890ff" className="tab-badge">
            {stats.totalBookings || 0}
          </TabBadge>
        </Space>
      ),
      children: <BookingManager initialEventTypeFilter={bookingEventTypeFilter} onStatsChange={loadDashboardStats} stats={stats} />
    },
    {
      key: 'availability',
      label: (
        <Space>
          <FontAwesomeIcon icon={faCalendar} />
          Availability
        </Space>
      ),
      children: <AvailabilityManager />
    },
    {
      key: 'event-types',
      label: (
        <Space>
          <FontAwesomeIcon icon={faBookmark} />
          Event Types
        </Space>
      ),
      children: <EventTypeManager />
    },
    {
      key: 'groups',
      label: (
        <Space>
          <FontAwesomeIcon icon={faLayerGroup} />
          Groups
        </Space>
      ),
      children: <EventTypeGroupManager />
    },
    {
      key: 'settings',
      label: (
        <Space>
          <FontAwesomeIcon icon={faCog} />
          Settings
        </Space>
      ),
      children: <AppointmentSettings />
    }
  ];

  return (
      <PageContainer>
        <StatsCards>
          <style>
            {`
              @media (max-width: 768px) {
                .booking-stats-card .ant-card-body {
                  padding: 16px !important;
                }
                .booking-stats-compact {
                  flex-direction: column !important;
                  align-items: stretch !important;
                  gap: 12px !important;
                }
                .booking-stats-numbers {
                  flex-wrap: wrap !important;
                  gap: 8px 20px !important;
                  justify-content: space-between !important;
                }
                .booking-stats-numbers > div {
                  min-width: calc(50% - 12px) !important;
                  flex: 0 0 auto !important;
                }
                .booking-stats-divider {
                  display: none !important;
                }
                .booking-stats-expand-btn {
                  width: 100% !important;
                  justify-content: center !important;
                  margin-top: 8px !important;
                }
              }
            `}
          </style>
          <Card 
            className="booking-stats-card"
            style={{ 
              marginBottom: '24px',
              borderRadius: '8px',
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
              position: 'relative'
            }}
            bodyStyle={{ padding: '24px' }}
          >
            {loading && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 10,
                borderRadius: '8px'
              }}>
                <HeartLoader size={40} />
              </div>
            )}
              {/* Compact View - Single Row */}
              <div 
                className="booking-stats-compact"
                style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px',
                  paddingBottom: isStatsExpanded ? '20px' : '0',
                  marginBottom: isStatsExpanded ? '20px' : '0',
                  borderBottom: isStatsExpanded ? '1px solid #f0f0f0' : 'none'
                }}>
                <div className="booking-stats-numbers" style={{ display: 'flex', alignItems: 'center', gap: '24px', flex: 1 }}>
                  <div>
                    <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '4px' }}>Total</div>
                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1890ff' }}>
                      {stats.totalBookings}
                    </div>
                  </div>
                  <div className="booking-stats-divider" style={{ width: '1px', height: '40px', background: '#f0f0f0' }} />
                  <div>
                    <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '4px' }}>Confirmed</div>
                    <div style={{ fontSize: '20px', fontWeight: '600', color: '#52c41a' }}>
                      {stats.confirmedBookings}
                    </div>
                  </div>
                  <div className="booking-stats-divider" style={{ width: '1px', height: '40px', background: '#f0f0f0' }} />
                  <div>
                    <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '4px' }}>Completed</div>
                    <div style={{ fontSize: '20px', fontWeight: '600', color: '#262626' }}>
                      {stats.completedBookings}
                    </div>
                  </div>
                  <div className="booking-stats-divider" style={{ width: '1px', height: '40px', background: '#f0f0f0' }} />
                  <div>
                    <div style={{ fontSize: '14px', color: '#faad14', marginBottom: '4px' }}>Pending</div>
                    <div style={{ fontSize: '20px', fontWeight: '600', color: '#faad14' }}>
                      {stats.pendingBookings}
                    </div>
                  </div>
                  <div className="booking-stats-divider" style={{ width: '1px', height: '40px', background: '#f0f0f0' }} />
                  <div>
                    <div style={{ fontSize: '14px', color: '#8c8c8c', marginBottom: '4px' }}>Cancelled</div>
                    <div style={{ fontSize: '20px', fontWeight: '600', color: '#ff4d4f' }}>
                      {stats.cancelledBookings}
                    </div>
                  </div>
                </div>
                <button
                  className="booking-stats-expand-btn"
                  onClick={() => setIsStatsExpanded(!isStatsExpanded)}
                  style={{
                    background: 'transparent',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '4px 10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '13px',
                    color: '#595959',
                    height: '32px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#1890ff';
                    e.currentTarget.style.color = '#1890ff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#d9d9d9';
                    e.currentTarget.style.color = '#595959';
                  }}
                >
                  <FontAwesomeIcon icon={isStatsExpanded ? faChevronUp : faChevronDown} />
                  {isStatsExpanded ? 'Collapse' : 'Expand'}
                </button>
              </div>

              {/* Expanded View - Event Locations */}
              {isStatsExpanded && (
                <div>
                  <div style={{ 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    color: '#262626',
                    marginBottom: '12px'
                  }}>
                    By Location
                  </div>
                <div style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  flexWrap: 'wrap'
                }}>
                  {stats.eventTypeStats && stats.eventTypeStats.length > 0 ? (
                    stats.eventTypeStats.map((eventType) => {
                      const isSelected = bookingEventTypeFilter === eventType._id;
                      return (
                        <div
                          key={eventType._id}
                          onClick={() => handleEventTypeClick(eventType._id)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 16px',
                            background: isSelected ? eventType.color || '#1890ff' : '#f5f5f5',
                            borderRadius: '20px',
                            fontSize: '14px',
                            border: `1px solid ${isSelected ? eventType.color || '#1890ff' : '#e8e8e8'}`,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow: isSelected ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.background = eventType.color || '#1890ff';
                              e.currentTarget.style.borderColor = eventType.color || '#1890ff';
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                              const nameSpan = e.currentTarget.querySelector('.name-span');
                              const countSpan = e.currentTarget.querySelector('.count-span');
                              if (nameSpan) nameSpan.style.color = 'white';
                              if (countSpan) countSpan.style.color = 'white';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.background = '#f5f5f5';
                              e.currentTarget.style.borderColor = '#e8e8e8';
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                              const nameSpan = e.currentTarget.querySelector('.name-span');
                              const countSpan = e.currentTarget.querySelector('.count-span');
                              if (nameSpan) nameSpan.style.color = '#595959';
                              if (countSpan) countSpan.style.color = eventType.color || '#1890ff';
                            }
                          }}
                        >
                          <span style={{ 
                            width: '8px', 
                            height: '8px', 
                            borderRadius: '50%',
                            background: isSelected ? 'white' : eventType.color || '#1890ff'
                          }} />
                          <span className="name-span" style={{ color: isSelected ? 'white' : '#595959' }}>{eventType.name}</span>
                          <span className="count-span" style={{ 
                            fontWeight: '600', 
                            color: isSelected ? 'white' : eventType.color || '#1890ff',
                            marginLeft: '4px'
                          }}>
                            {eventType.bookingCount || 0}
                          </span>
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ color: '#8c8c8c', fontSize: '14px' }}>
                      No event locations available
                    </div>
                  )}
                </div>
                </div>
              )}
          </Card>
        </StatsCards>

        {/* Hidden original cards section for reference */}
        <div style={{ display: 'none' }}>
          <div style={{ 
            overflowX: 'auto',
            overflowY: 'hidden',
            paddingBottom: '8px',
            scrollbarWidth: 'thin',
            scrollbarColor: '#d1d5db #f9fafb'
          }}>
            <div style={{ 
              display: 'flex', 
              gap: '24px', 
              width: '100%'
            }}>
              {/* Event Type Cards - Dynamic based on available event types */}
              {stats.eventTypeStats && stats.eventTypeStats.length > 0 && (
                stats.eventTypeStats.map((eventType) => (
                  <div key={eventType._id} style={{ flex: 1, minWidth: '150px' }}>
                    <StatsCard>
                      <Card style={{ height: '140px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }} bodyStyle={{ padding: '12px 16px', width: '100%', overflow: 'hidden' }}>
                        <Spin spinning={loading}>
                          <div style={{ padding: '0', width: '100%', overflow: 'hidden' }}>
                            <StatNumber style={{ 
                              color: eventType.color || '#1890ff',
                              fontSize: '24px',
                              fontWeight: 'bold'
                            }}>
                              {eventType.bookingCount || 0}
                            </StatNumber>
                            <StatLabel style={{ 
                              marginTop: '8px', 
                              minHeight: '20px', 
                              display: '-webkit-box',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 2,
                              alignItems: 'center', 
                              justifyContent: 'center',
                              overflow: 'hidden',
                              width: '100%',
                              maxWidth: '100%',
                              padding: '0 4px',
                              lineHeight: '1.2',
                              textAlign: 'center'
                            }}>
                              {eventType.name}
                            </StatLabel>
                          </div>
                        </Spin>
                      </Card>
                    </StatsCard>
                  </div>
                ))
              )}

              {/* Active Bookings Card - Before Completed */}
              <div style={{ flex: 1, minWidth: '150px' }}>
                <StatsCard>
                  <Card style={{ height: '140px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Spin spinning={loading}>
                      <div style={{ padding: '12px 0' }}>
                        <StatNumber style={{ color: '#52c41a', fontSize: '24px', fontWeight: 'bold' }}>{stats.activeBookings}</StatNumber>
                        <StatLabel style={{ marginTop: '8px', minHeight: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          Active Bookings
                        </StatLabel>
                      </div>
                    </Spin>
                  </Card>
                </StatsCard>
              </div>

              {/* Completed Bookings Card - Always Last */}
              <div style={{ flex: 1, minWidth: '150px' }}>
                <StatsCard>
                  <Card style={{ height: '140px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Spin spinning={loading}>
                      <div style={{ padding: '12px 0' }}>
                        <StatNumber style={{ color: '#722ed1', fontSize: '24px', fontWeight: 'bold' }}>{stats.completedBookings}</StatNumber>
                        <StatLabel style={{ marginTop: '8px', minHeight: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          Completed Bookings
                        </StatLabel>
                      </div>
                    </Spin>
                  </Card>
                </StatsCard>
              </div>
            </div>
          </div>
        </div>

        <StyledTabs
          activeKey={activeTab}
          onChange={setActiveTab}
          size="large"
          items={tabItems}
          style={{ marginTop: 0, marginBottom: 24 }}
        />
      </PageContainer>
  );
});

export default NewEventManagement;